// ═══════════════════════════════════════════════════════════════
// ChroniclesAdmin — Gestion éditoriale des Chroniques ITRIIA
// Admin only. CRUD complet sur interface_equipe.itriia_chronicles.
// Session Greg NEXT · chantier C.3.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import {
  Plus, Save, X, Edit2, Trash2, Eye, EyeOff, Loader, ScrollText,
  Users, Brain, Hammer, Languages, Sparkles, BookOpen, Shield, Globe,
  Layers, FlaskConical, Target, MessagesSquare, Share2, Lock, Compass, Wand2,
} from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const API_BASE = '/api/chronicles';
const authHeaders = () => {
  const token = localStorage.getItem('oracle_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ICON_MAP = {
  users: Users, brain: Brain, hammer: Hammer, languages: Languages,
  sparkles: Sparkles, book: BookOpen, shield: Shield, globe: Globe,
  layers: Layers, flask: FlaskConical, target: Target, chat: MessagesSquare,
  share: Share2, lock: Lock, compass: Compass, magic: Wand2,
};
const ICON_NAMES = Object.keys(ICON_MAP);

function ChronIcon({ name, size = 16 }) {
  const Cmp = ICON_MAP[name] || Sparkles;
  return <Cmp size={size} strokeWidth={1.7} />;
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

const ACCENT = '#8b5cf6';

export default function ChroniclesAdmin() {
  const [chronicles, setChronicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // 'new' | id | null
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Accès refusé');
      setChronicles(await res.json());
    } catch (err) {
      toast.error(err.message || 'Erreur chargement chroniques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm({
      publish_date: todayISO(),
      title: '',
      teaser: '',
      icon: 'sparkles',
      slug: '',
      visible_to: '',
      is_published: true,
    });
    setEditingId('new');
  };

  const startEdit = (c) => {
    setForm({
      publish_date: c.publish_date ? String(c.publish_date).split('T')[0] : todayISO(),
      title: c.title || '',
      teaser: c.teaser || '',
      icon: c.icon || 'sparkles',
      slug: c.slug || '',
      visible_to: Array.isArray(c.visible_to) ? c.visible_to.join(', ') : '',
      is_published: c.is_published !== false,
    });
    setEditingId(c.id);
  };

  const cancel = () => { setEditingId(null); setForm(null); };

  const save = async () => {
    if (!form.title.trim() || !form.teaser.trim()) {
      toast.warning('Titre et teaser requis');
      return;
    }
    const payload = {
      publish_date: form.publish_date,
      title: form.title.trim(),
      teaser: form.teaser.trim(),
      icon: form.icon || null,
      slug: form.slug.trim() || null,
      visible_to: form.visible_to.split(',').map(s => s.trim()).filter(Boolean),
      is_published: !!form.is_published,
    };
    try {
      const url = editingId === 'new' ? API_BASE : `${API_BASE}/${editingId}`;
      const method = editingId === 'new' ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erreur sauvegarde');
      }
      toast.success(editingId === 'new' ? 'Chronique créée' : 'Chronique mise à jour');
      cancel();
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const togglePublish = async (c) => {
    try {
      const res = await fetch(`${API_BASE}/${c.id}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_published: !c.is_published }),
      });
      if (!res.ok) throw new Error('Erreur toggle publication');
      toast.success(c.is_published ? 'Dépubliée' : 'Publiée');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (c) => {
    const ok = await confirmModal(`Dépublier la chronique « ${c.title} » ? Elle ne sera plus visible par les users mais la trace est conservée en base.`, { confirmText: 'Dépublier', danger: true });
    if (!ok) return;
    try {
      const res = await fetch(`${API_BASE}/${c.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Erreur suppression');
      toast.success('Chronique dépubliée');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px', fontSize: 13,
    background: 'var(--input-bg)', color: 'var(--text)',
    border: '1px solid var(--card-border)', borderRadius: 8,
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
  };

  const btnPrimary = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '9px 16px', fontSize: 13, fontWeight: 600,
    background: `linear-gradient(135deg, ${ACCENT}, #7c3aed)`, color: '#fff',
    border: 'none', borderRadius: 9, cursor: 'pointer',
    boxShadow: `0 4px 14px ${ACCENT}40`, transition: 'all 0.18s',
  };

  const btnGhost = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', fontSize: 12,
    background: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--card-border)', borderRadius: 8, cursor: 'pointer',
  };

  const iconButton = (active) => ({
    padding: 7, borderRadius: 7, cursor: 'pointer',
    background: active ? `${ACCENT}25` : 'var(--input-bg)',
    border: `1px solid ${active ? ACCENT : 'var(--card-border)'}`,
    color: active ? ACCENT : 'var(--text-secondary)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s',
  });

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <ScrollText size={22} style={{ color: ACCENT }} />
            Chroniques ITRIIA
          </h2>
          <p style={{ margin: '6px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Fil éditorial interne visible dans la sidebar de chaque user.
          </p>
        </div>
        {!editingId && (
          <button onClick={startNew} style={btnPrimary}>
            <Plus size={15} /> Nouvelle chronique
          </button>
        )}
      </div>

      {/* Formulaire edit / create */}
      {editingId && form && (
        <div style={{
          background: 'var(--card-bg)', border: `1px solid ${ACCENT}40`,
          borderRadius: 14, padding: 22, marginBottom: 24,
          boxShadow: `0 4px 28px ${ACCENT}20`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
              {editingId === 'new' ? 'Nouvelle chronique' : `Éditer chronique #${editingId}`}
            </h3>
            <button onClick={cancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} title="Annuler">
              <X size={18} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3, alignSelf: 'center' }}>Date</label>
            <input type="date" value={form.publish_date} onChange={e => setForm({ ...form, publish_date: e.target.value })} style={{ ...inputStyle, maxWidth: 200 }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3, alignSelf: 'center' }}>Titre</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} maxLength={200} placeholder="Titre éditorial concis" style={inputStyle} />

            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3, alignSelf: 'start', paddingTop: 10 }}>Teaser</label>
            <textarea value={form.teaser} onChange={e => setForm({ ...form, teaser: e.target.value })} rows={3} placeholder="Phrase italique affichée sous le titre (markdown autorisé)" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3, alignSelf: 'start', paddingTop: 10 }}>Icône</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ICON_NAMES.map(name => (
                <button key={name} type="button" title={name} onClick={() => setForm({ ...form, icon: name })} style={iconButton(form.icon === name)}>
                  <ChronIcon name={name} size={15} />
                </button>
              ))}
            </div>

            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3, alignSelf: 'center' }}>Slug (opt.)</label>
            <input type="text" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="url-friendly-slug" style={inputStyle} />

            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3, alignSelf: 'center' }}>Visible pour</label>
            <input type="text" value={form.visible_to} onChange={e => setForm({ ...form, visible_to: e.target.value })} placeholder="vide = tout le monde · sinon usernames séparés par virgule (ex : greg, pierre)" style={inputStyle} />

            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3, alignSelf: 'center' }}>Publier</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Visible dans la sidebar des users</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
            <button onClick={cancel} style={btnGhost}>Annuler</button>
            <button onClick={save} style={btnPrimary}>
              <Save size={14} /> {editingId === 'new' ? 'Créer' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader size={22} className="expertise-spin" />
          <div style={{ fontSize: 12, marginTop: 10 }}>Chargement…</div>
        </div>
      ) : chronicles.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', border: '1px dashed var(--card-border)', borderRadius: 12 }}>
          <ScrollText size={28} style={{ opacity: 0.4 }} />
          <div style={{ fontSize: 13, marginTop: 10 }}>Aucune chronique pour l'instant — clique sur « Nouvelle chronique » pour en créer une.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {chronicles.map(c => (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 16px',
              background: 'var(--card-bg)',
              border: `1px solid ${c.is_published ? 'var(--card-border)' : 'rgba(239,68,68,0.25)'}`,
              borderRadius: 11, opacity: c.is_published ? 1 : 0.72,
              transition: 'all 0.15s',
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                background: `${ACCENT}15`, color: ACCENT,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ChronIcon name={c.icon} size={17} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                    {c.publish_date ? String(c.publish_date).split('T')[0] : '—'}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{c.title}</span>
                  {!c.is_published && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', background: 'rgba(239,68,68,0.12)', padding: '2px 7px', borderRadius: 5 }}>
                      DÉPUBLIÉE
                    </span>
                  )}
                  {Array.isArray(c.visible_to) && c.visible_to.length > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: ACCENT, background: `${ACCENT}15`, padding: '2px 7px', borderRadius: 5 }}>
                      {c.visible_to.length} destinataire(s)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: 3,
                  overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {c.teaser}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => togglePublish(c)} title={c.is_published ? 'Dépublier' : 'Publier'} style={{
                  padding: 7, borderRadius: 7, cursor: 'pointer',
                  background: 'var(--input-bg)', border: '1px solid var(--card-border)',
                  color: c.is_published ? '#10b981' : 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {c.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button onClick={() => startEdit(c)} title="Éditer" style={{
                  padding: 7, borderRadius: 7, cursor: 'pointer',
                  background: 'var(--input-bg)', border: '1px solid var(--card-border)',
                  color: 'var(--text-secondary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Edit2 size={14} />
                </button>
                <button onClick={() => remove(c)} title="Dépublier (soft-delete)" style={{
                  padding: 7, borderRadius: 7, cursor: 'pointer',
                  background: 'var(--input-bg)', border: '1px solid var(--card-border)',
                  color: '#ef4444',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
